<script setup>
import { ref, watch, onMounted, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from "primevue/usetoast";
import { callAPI, DRUPAL_API_ROUTE } from "@/service/DrupalService";
import { useI18n } from 'vue-i18n'

const router = useRouter();

const { t } = useI18n();

const toast = useToast();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const emit = defineEmits(['update:visible']);

const localVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});
const crsf = ref();
const user = ref(JSON.parse(localStorage.getItem('user')));

const initialState = {
  domainsLoading: false,
  domain: '',
  domainsList: [],
  domainDisabled: false,
  bulkFileUploadURLBase: DRUPAL_API_ROUTE.BULK_UPLOAD,
  bulkFileUploadURL: '',
  bulkFile: '',
  bulkCreateLoading: false,
  bulksList: [],
  bulkRunning: false,
  bulksUploading: false
};

const formData = reactive({ ...initialState });

const home = ref({
  icon: 'pi pi-home'
});
const items = ref([
  {
    label: t('bulk.title')
  }
]);

const getBulks = async () => {
  var response = await callAPI(DRUPAL_API_ROUTE.BULKS);
  formData.bulksList = response.data;

  formData.bulkRunning = formData.bulksList.find(bulk => bulk.status === 0) !== undefined;
};


const fetchDomains = async (refresh = false) => {
  var currentWorkspace = user.value.current_workspace;

  if (refresh) {
    formData.domainsLoading = true;
    var response = await callAPI(DRUPAL_API_ROUTE.WORKSPACES_DOMAINS_LIST, { workspace: currentWorkspace, type: 'shortlinks' });
    formData.domainsList = response.data;
    formData.domainsLoading = false;
  } else {
    formData.domainsList = JSON.parse(localStorage.getItem('shortlinksDomains'));
  }

  var workspaceData = JSON.parse(localStorage.getItem('workspaces'));
  var currentWorkspaceData = workspaceData.find(item => item.id === currentWorkspace);
  if (currentWorkspaceData.default_domain) formData.domain = currentWorkspaceData.default_domain;
};

const onProgress = () => {
  formData.bulksUploading = true;
}

const onUpload = async (event) => {
  var response = JSON.parse(event.xhr.response);

  formData.bulkFile = response;
  formData.bulksUploading = false;
  toast.add({ severity: 'success', summary: t('bulk.title'), detail: t('bulk.upload.messages.validation'), life: 5000 });
};

const onErrorSend = async (event) => {
  formData.bulksUploading = false;
  var response = JSON.parse(event.xhr.response);
  toast.add({ severity: 'error', summary: t('bulk.title'), detail: response.error, life: 5000 });
}

const bulkGenerate = async () => {
  if (formData.bulkCreateLoading || formData.bulksUploading || !formData.bulkFile) return;

  formData.bulkCreateLoading = true;

  await callAPI(DRUPAL_API_ROUTE.BULK_CREATE, {}, { domain: formData.domain });

  router.push({
    path: '/shortlinks',
    query: { openBulk: 'true' }
  });

  handleHide();
}

const bulkTemplateDownload = async (uuid) => {
  var response = await callAPI(DRUPAL_API_ROUTE.BULK_TEMPLATE);
  var fileUrl = response.data;

  var link = document.createElement('a');
  link.href = fileUrl;
  link.click();
}

const clearCallback = async () => {
  formData.bulkFile = '';
}

function resetForm() {
  Object.assign(formData, initialState);
}

async function handleShow() {
  await fetchDomains();
  await getBulks();

  if (!formData.bulkRunning) {
    var response = await callAPI(DRUPAL_API_ROUTE.CRSF_TOKEN);
    crsf.value = response.data;
  }
}

async function onBeforeSend(request) {
  request.xhr.setRequestHeader('X-CSRF-Token', crsf.value);
}

function handleHide() {
  resetForm();
  emit('update:visible', false);
}

watch(() => formData.domain, (newDomain) => {
  formData.bulkFileUploadURL = formData.bulkFileUploadURLBase + '?domain=' + encodeURIComponent(newDomain);
});
</script>

<template>
  <Fluid>
    <Drawer :blockScroll="true" :dismissable="false" @hide="handleHide" @show="handleShow"
      v-model:visible="localVisible">
      <template #header>
        <div class="flex items-start gap-2">
          <Breadcrumb :home="home" :model="items" class="!p-0">
            <template #item="{ item }">
              <span v-if="item.label" class="text-color mr-2">{{ item.label }}</span>
              <span :class="item.icon"></span>
            </template>
            <template #separator><i class="pi pi-angle-right"></i></template>
          </Breadcrumb>
        </div>
      </template>
      <div class="flex mx-auto">
        <div class="flex flex-col">
          <form @submit.prevent="generatePage" class="space-y-10">
            <div class="flex flex-col gap-2 space-y-4">
              <div class="space-y-6">
                <div v-if="!formData.bulkFile" class="flex flex-col items-start gap-2">
                  <label for="notice">
                    {{ t('bulk.messages.notice') }}
                  </label>
                  <div><Button class="w-1/2" icon="pi pi-download" @click="bulkTemplateDownload"
                      :label="t('common.labels.download')" />
                  </div>
                </div>
                <div class="flex flex-col gap-2">
                  <label for="domain">{{ t('bulk.labels.domain') }}</label>
                  <InputGroup>
                    <InputGroupAddon>
                      <Button icon="pi pi-refresh" v-tooltip.bottom="t('common.labels.refresh')" severity="secondary"
                        @click="fetchDomains(true)" :disabled="formData.bulkRunning" />
                    </InputGroupAddon>
                    <Select v-model="formData.domain" :options="formData.domainsList" optionValue="uuid"
                      optionLabel="domain" :placeholder="t('bulk.placeholder.domain')"
                      :disabled="formData.domainDisabled || formData.bulkRunning" :loading="formData.domainsLoading" />
                  </InputGroup>
                </div>
              </div>
              <div class="flex flex-col gap-2 space-y-4">
                <div class="space-y-6">
                  <div class="flex flex-col gap-4">
                    <div class="flex flex-col items-start gap-2">
                      <label for="domain">{{ t('bulk.labels.file') }}</label>
                      <div v-if="!formData.bulkFile">
                        <FileUpload @progress="onProgress" @error="onErrorSend" @before-send="onBeforeSend"
                          :withCredentials="true" mode="basic" name="file" :url="formData.bulkFileUploadURL"
                          accept="text/csv" :maxFileSize="1000000" @upload="onUpload" :auto="true"
                          :chooseLabel="t('common.labels.browse')"
                          :disabled="formData.bulksUploading || !formData.domain || formData.bulkRunning" />
                        <ProgressBar v-if="formData.bulksUploading" mode="indeterminate" class="w-24 mt-5"
                          style="height: 6px">
                        </ProgressBar>
                      </div>
                      <div v-else class="flex items-center space-x-4">
                        <div>{{ formData.bulkFile }}</div>
                        <div><Button @click="clearCallback()" icon="pi pi-times" rounded outlined
                            severity="danger"></Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="formData.bulkRunning">
                <Message severity="warn">{{ t('bulk.messages.current') }}</Message>
              </div>
            </div>
          </form>
        </div>
      </div>
      <template #footer>
        <div v-if="!formData.bulkRunning" class="flex items-center gap-2">
          <Button @click="handleHide" :label="t('common.labels.cancel')" class="flex-auto" outlined />
          <Button type="submit" @click="bulkGenerate" :loading="formData.bulkCreateLoading || formData.bulksUploading"
            :label="t('common.labels.create')" :disabled="!formData.bulkFile" />
        </div>
        <div v-else>
          <Button @click="handleHide" :label="t('common.labels.close')" class="flex-auto" outlined />
        </div>
      </template>
    </Drawer>
  </Fluid>
</template>
